import { PrimaryButton, SecondaryButton } from 'apps/wrapper/src/components/shared/buttons/buttons.styles';
import { useFeatureFlags } from 'apps/wrapper/src/hooks/feature-flags/use-feature-flags';
import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { AppointmentRequestSourceFilter } from 'apps/wrapper/src/services/appointment/types/appointment-request-source-filter';
import { AppointmentRequestStatusFilter } from 'apps/wrapper/src/services/appointment/types/appointment-request-status-filter';
import { AppointmentRequestTypeFilter } from 'apps/wrapper/src/services/appointment/types/appointment-request-type-filter';
import { FC, useEffect, useMemo, useState } from 'react';
import { useApptRequestsWidget } from '../../hooks/use-appt-requests-widget';
import { Page, PageContent, PageTitle, Toolbar } from '../styles';
import { FilterSectionComponent } from './filter-section';
import { useFilterLogic } from './use-filter-logic';

export const FiltersView: FC = () => {
  const { goBack, appointmentRequestsFilter, setAppointmentRequestsFilter } = useApptRequestsWidget();
  const [tmpFilter, setTmpFilter] = useState(appointmentRequestsFilter);
  const { selectedLocation } = useLocations();
  const { hasFeature } = useFeatureFlags();
  const hasAiScheduling = hasFeature(selectedLocation!.id, 'AIscheduling');
  const hasDirectBookingFeature = hasFeature(selectedLocation!.id, 'DirectBooking');
  const { handleFilterChange, clearAllFilters } = useFilterLogic(tmpFilter, setTmpFilter);

  useEffect(() => {
    setTmpFilter(appointmentRequestsFilter);
  }, [appointmentRequestsFilter]);

  const typeOptions = useMemo(
    () =>
      [
        {
          value: AppointmentRequestTypeFilter.DirectScheduled,
          label: 'Directly Scheduled',
          visible: hasDirectBookingFeature
        },
        { value: AppointmentRequestTypeFilter.AppointmentRequests, label: 'Appointment requests' },
        { value: AppointmentRequestTypeFilter.Cancellation, label: 'Cancellation', visible: hasAiScheduling },
        { value: AppointmentRequestTypeFilter.Rescheduling, label: 'Rescheduling', visible: hasAiScheduling }
      ].filter((option) => option.visible !== false),
    [hasAiScheduling, hasDirectBookingFeature]
  );

  const statusOptions = useMemo(
    () => [
      { value: AppointmentRequestStatusFilter.Pending, label: 'Pending' },
      { value: AppointmentRequestStatusFilter.Approved, label: 'Approved' },
      { value: AppointmentRequestStatusFilter.Dismissed, label: 'Dismissed' }
    ],
    []
  );

  const sourceOptions = useMemo(
    () => [
      { value: AppointmentRequestSourceFilter.AI, label: 'RW AI', visible: hasAiScheduling },
      { value: AppointmentRequestSourceFilter.AppointmentForm, label: 'Appointment Form' },
      { value: AppointmentRequestSourceFilter.WebsiteWidget, label: 'Website Widget' }
    ],
    [hasAiScheduling]
  );

  const handleCancelClick = () => {
    goBack();
  };

  const handleClearAllClick = () => {
    clearAllFilters();
  };

  const handleApplyClick = async () => {
    setAppointmentRequestsFilter(tmpFilter);
    goBack();
  };

  // Return null if filter is not initialized yet
  if (!appointmentRequestsFilter || !tmpFilter) return null;

  return (
    <Page>
      <PageTitle>Filter alerts</PageTitle>
      <PageContent sx={{ margin: '15px 20px 0 20px' }}>
        <FilterSectionComponent
          title="Type"
          options={typeOptions}
          selectedValues={tmpFilter.requestType || []}
          onChange={(value, checked) => handleFilterChange('type', value, checked)}
        />

        <FilterSectionComponent
          title="Status"
          options={statusOptions}
          selectedValues={tmpFilter.requestStatus || []}
          onChange={(value, checked) => handleFilterChange('status', value, checked)}
        />

        <FilterSectionComponent
          title="Source"
          options={sourceOptions}
          selectedValues={tmpFilter.source || []}
          onChange={(value, checked) => handleFilterChange('source', value, checked)}
        />

        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <SecondaryButton onClick={handleCancelClick} disableElevation>
            Cancel
          </SecondaryButton>
          <SecondaryButton onClick={handleClearAllClick} disableElevation>
            Clear All
          </SecondaryButton>
          <PrimaryButton onClick={handleApplyClick} disableElevation>
            Apply
          </PrimaryButton>
        </Toolbar>
      </PageContent>
    </Page>
  );
};
